import React, { useState, useEffect, useRef, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { ApiService, DistributionNotificationService } from "../../../../../../api";
import { translate } from "../../../../../../common/providers";
import styles from "./NotificationHistory.module.css";
import HistoryRow from "./HistoryRow";

const PAGE_SIZE = 10;

export default function NotificationHistory({ projectId }) {
	const [history, setHistory] = useState([]);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [initialLoad, setInitialLoad] = useState(true);
	const cancelTokenSourceRef = useRef(null);
	const scrollContainerRef = useRef(null);
	const pageRef = useRef(0);
	const loadingRef = useRef(false);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	const loadHistory = useCallback(
		(pageToLoad) => {
			if (loadingRef.current) {
				return;
			}
			loadingRef.current = true;
			setLoading(true);
			const token = cancelTokenSourceRef.current?.token;
			DistributionNotificationService.getHistory(projectId, { page: pageToLoad, limit: PAGE_SIZE }, token)
				.then((data) => {
					const newItems = data.contents || [];
					setHistory((prev) => (pageToLoad === 0 ? newItems : [...prev, ...newItems]));
					setHasMore(pageToLoad + 1 < (data.totalPages || 0));
					setInitialLoad(false);
				})
				.catch((err) => {
					console.error("Error fetching history:", err);
					setInitialLoad(false);
				})
				.finally(() => {
					loadingRef.current = false;
					setLoading(false);
				});
		},
		[projectId]
	);

	useEffect(() => {
		if (projectId) {
			setHistory([]);
			pageRef.current = 0;
			setHasMore(true);
			setInitialLoad(true);
			loadHistory(0);
		}
	}, [projectId, loadHistory]);

	const handleScroll = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container || loadingRef.current || !hasMore) {
			return;
		}
		const { scrollTop, scrollHeight, clientHeight } = container;
		if (scrollHeight - scrollTop - clientHeight < 100) {
			pageRef.current += 1;
			loadHistory(pageRef.current);
		}
	}, [hasMore, loadHistory]);

	if (initialLoad && loading) {
		return <div className={styles.loading}>{translate("common:results.loading")}</div>;
	}

	if (history.length === 0 && !loading) {
		return (
			<div className={styles.emptyContainer}>
				<div className={styles.emptyTitle}>
					{translate("document-center.distribution-notification.history.empty-title")}
				</div>
				<div className={styles.emptySubtitle}>
					{translate("document-center.distribution-notification.history.empty-subtitle")}
				</div>
			</div>
		);
	}
	return (
		<div ref={scrollContainerRef} className={styles.cardList} onScroll={handleScroll}>
			{history.map((item) => (
				<HistoryRow key={item.id} item={item} />
			))}
			{loading && (
				<div className={styles.loadingMore}>
					<CircularProgress size={24} />
				</div>
			)}
		</div>
	);
}
